import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ProductForm from '../../components/products/ProductForm';
import productService from '../../services/productService';
import mediaService from '../../services/mediaService';
import './ProductFormPage.css';

function ProductCreatePage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Upload new images and return array with server URLs
   */
  const uploadNewImages = async (images) => {
    if (!images || images.length === 0) {
      return [];
    }

    const uploadPromises = images.map(async (img, index) => {
      // New image - needs upload
      if (img.isNew && img.file) {
        try {
          const uploaded = await mediaService.upload(
            img.file,
            'products',
            img.altText || ''
          );
          return {
            url: uploaded.url,
            isPrimary: img.isPrimary,
            displayOrder: index,
            altText: img.altText || '',
          };
        } catch (uploadError) {
          console.error('Failed to upload image:', img.file.name, uploadError);
          throw new Error(`Failed to upload image: ${img.file.name}`);
        }
      }

      // Existing image (shouldn't happen on create, but handle gracefully)
      return {
        id: img.id,
        url: img.url,
        isPrimary: img.isPrimary,
        displayOrder: index,
        altText: img.altText || '',
      };
    });

    return Promise.all(uploadPromises);
  };

  const handleSubmit = async (data) => {
    setIsSubmitting(true);

    try {
      // Step 1: Upload new images to server
      const uploadedImages = await uploadNewImages(data.images);

      // Step 2: Create product with uploaded image URLs
      const productData = {
        ...data,
        images: uploadedImages,
      };

      const response = await productService.createProduct(productData);

      // Step 3: Map backend sortOrder to frontend displayOrder
      if (response.data?.images && Array.isArray(response.data.images)) {
        response.data.images = response.data.images.map(img => ({
          ...img,
          displayOrder: img.sortOrder  // Map sortOrder → displayOrder
        }));
      }

      toast.success('Product created successfully');
      navigate(`/products/${response.data.id}`);
    } catch (err) {
      // Display user-friendly error message
      // err.message is already sanitized by mediaService or other services
      const message = err.message || 'Failed to create product. Please try again.';
      toast.error(message);

      // Log full error for debugging (not shown to user)
      console.error('Error creating product:', err.originalError || err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="product-form-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Add New Product</h1>
          <p>Create a new product for your catalog</p>
        </div>
      </div>

      <ProductForm onSubmit={handleSubmit} isSubmitting={isSubmitting} />
    </div>
  );
}

export default ProductCreatePage;
